/**
 * PWA Install Prompt for admin/booth (MusicBox / Master Control Center).
 *
 * Why this exists
 * ---------------
 * Android Chrome aggressively kills background tabs to free memory. When
 * the DJ Console runs inside a browser tab, the OS treats it like any
 * other page — a phone call, a switch to WhatsApp, or the screen locking
 * can kill it. But once the same URL is INSTALLED as a PWA (Add to Home
 * Screen), Android treats it as an app and keeps it alive far longer,
 * respecting media focus. This is the single biggest win for the "stops
 * after every song" class of bugs after wake-lock hardening.
 *
 * UX
 * --
 * We wait for the browser's own `beforeinstallprompt` event (Chrome,
 * Edge, Android WebView). We DON'T show anything on the very first
 * visit — that's an anti-pattern. Instead we quietly increment a visit
 * counter in localStorage and only surface a slim banner on the 2nd
 * qualifying visit. Users can dismiss the banner; we then wait 7 days
 * before showing it again. Once installed (browser fires `appinstalled`)
 * we set a permanent flag and never nag again.
 *
 * iOS Safari
 * ----------
 * Safari doesn't fire beforeinstallprompt. When we detect iOS Safari
 * that ISN'T already in standalone mode, we fall back to a "Tap
 * Share → Add to Home Screen" hint the first time the user visits
 * Settings.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Download, X, Smartphone } from 'lucide-react';

const VISIT_KEY = 'mcc:pwa:visitCount';
const DISMISSED_AT_KEY = 'mcc:pwa:bannerDismissedAt';
const INSTALLED_KEY = 'mcc:pwa:installed';
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function isStandalone() {
  if (typeof window === 'undefined') return false;
  const displayMode = window.matchMedia?.('(display-mode: standalone)')?.matches;
  // iOS Safari uses `navigator.standalone`, not the media query.
  return Boolean(displayMode || window.navigator?.standalone);
}

function bumpVisit() {
  try {
    const raw = Number(localStorage.getItem(VISIT_KEY) || 0);
    const next = Number.isFinite(raw) ? raw + 1 : 1;
    localStorage.setItem(VISIT_KEY, String(next));
    return next;
  } catch (_) {
    return 1;
  }
}

function shouldShow(installEvt) {
  if (!installEvt) return false;
  try {
    if (localStorage.getItem(INSTALLED_KEY) === '1') return false;
    const dismissedAt = Number(localStorage.getItem(DISMISSED_AT_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_COOLDOWN_MS) return false;
    const visits = Number(localStorage.getItem(VISIT_KEY) || 0);
    return visits >= 2;
  } catch (_) {
    return true;
  }
}

/**
 * Bottom-of-screen slim banner. Renders itself only when appropriate.
 * Mounted once at the root of the authenticated admin shell.
 */
export const PWAInstallBanner = () => {
  const [installEvt, setInstallEvt] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Increment visit count exactly once per app boot.
    bumpVisit();

    if (isStandalone()) {
      try { localStorage.setItem(INSTALLED_KEY, '1'); } catch (_) { /* noop */ }
      return undefined;
    }

    const onBIP = (e) => {
      e.preventDefault();
      setInstallEvt(e);
    };
    const onInstalled = () => {
      try { localStorage.setItem(INSTALLED_KEY, '1'); } catch (_) { /* noop */ }
      setVisible(false);
      setInstallEvt(null);
    };
    window.addEventListener('beforeinstallprompt', onBIP);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBIP);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  useEffect(() => {
    if (shouldShow(installEvt)) {
      // Slight delay so the banner doesn't fight the initial page paint.
      const t = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [installEvt]);

  const install = useCallback(async () => {
    if (!installEvt) return;
    try {
      installEvt.prompt();
      const choice = await installEvt.userChoice;
      if (choice?.outcome === 'accepted') {
        try { localStorage.setItem(INSTALLED_KEY, '1'); } catch (_) { /* noop */ }
      } else {
        try { localStorage.setItem(DISMISSED_AT_KEY, String(Date.now())); } catch (_) { /* noop */ }
      }
    } catch (_) { /* user gesture rejected */ }
    setInstallEvt(null);
    setVisible(false);
  }, [installEvt]);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(DISMISSED_AT_KEY, String(Date.now())); } catch (_) { /* noop */ }
    setVisible(false);
  }, []);

  if (!visible || !installEvt) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9998] w-[min(92vw,480px)] rounded-2xl border border-[#FF007F]/40 bg-black/85 backdrop-blur-xl shadow-2xl overflow-hidden"
    >
      <div className="flex items-center gap-3 p-3 pr-2">
        <div className="w-10 h-10 rounded-full bg-[#FF007F]/20 flex items-center justify-center flex-shrink-0">
          <Download className="w-5 h-5 text-[#FF007F]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-white leading-tight">Install MusicBox on this device</p>
          <p className="text-[11px] text-white/60 mt-0.5 leading-snug">
            Keeps playback alive when the screen locks — no more mid-set stops.
          </p>
        </div>
        <button
          onClick={install}
          data-testid="pwa-install-accept"
          className="text-xs font-bold uppercase tracking-widest bg-[#FF007F] hover:bg-[#FF007F]/90 text-white px-3 py-2 rounded-lg whitespace-nowrap"
        >
          Install
        </button>
        <button
          onClick={dismiss}
          data-testid="pwa-install-dismiss"
          aria-label="Dismiss"
          className="w-8 h-8 flex items-center justify-center text-white/40 hover:text-white/80"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

/**
 * Settings card variant — always available in Settings so hosts can
 * install on demand even if they've dismissed the banner. Handles iOS
 * fallback text since Safari doesn't fire beforeinstallprompt.
 */
export const PWAInstallCard = () => {
  const [installEvt, setInstallEvt] = useState(null);
  const [installed, setInstalled] = useState(() => isStandalone());
  const [isIOS] = useState(() => {
    if (typeof navigator === 'undefined') return false;
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  });

  useEffect(() => {
    if (isStandalone()) {
      setInstalled(true);
      return undefined;
    }
    const onBIP = (e) => { e.preventDefault(); setInstallEvt(e); };
    const onInstalled = () => { setInstalled(true); setInstallEvt(null); };
    window.addEventListener('beforeinstallprompt', onBIP);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBIP);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!installEvt) return;
    try {
      installEvt.prompt();
      const choice = await installEvt.userChoice;
      if (choice?.outcome === 'accepted') setInstalled(true);
    } catch (_) { /* noop */ }
    setInstallEvt(null);
  }, [installEvt]);

  return (
    <div
      data-testid="pwa-install-card"
      className="rounded-2xl border border-white/10 bg-white/[0.02] p-5"
    >
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl bg-[#FF007F]/15 flex items-center justify-center flex-shrink-0">
          <Smartphone className="w-6 h-6 text-[#FF007F]" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-bold text-white">Install MusicBox on this device</h3>
          <p className="text-sm text-white/60 mt-1 leading-snug">
            Recommended for Android tablets. Installing as an app lets Android keep the music running when the screen locks — the single biggest fix for playback stopping mid-set.
          </p>
          {installed ? (
            <p
              data-testid="pwa-install-installed"
              className="mt-3 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#39FF14]"
            >
              ✓ Installed on this device
            </p>
          ) : installEvt ? (
            <button
              onClick={install}
              data-testid="pwa-install-card-btn"
              className="mt-3 inline-flex items-center gap-2 bg-[#FF007F] hover:bg-[#FF007F]/90 text-white font-bold text-sm px-4 py-2 rounded-lg"
            >
              <Download className="w-4 h-4" />
              Install now
            </button>
          ) : isIOS ? (
            <p className="mt-3 text-xs text-white/50 leading-snug">
              On iPad: tap the <strong className="text-white/80">Share</strong> icon in Safari, then <strong className="text-white/80">Add to Home Screen</strong>.
            </p>
          ) : (
            <p className="mt-3 text-xs text-white/40 leading-snug">
              Your browser doesn't offer a one-tap install prompt on this page yet. Try again after playing for a few minutes, or open the app menu (⋮) and choose <em>Install app</em>.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PWAInstallCard;
